/**
 * WallSketch Snap Visuals
 *
 * Renders the visual link between a snap and the geometry it was inferred
 * from. For each active construction line in a snap result, we may draw:
 *
 *  - A thin **dashed guide** through the line's origin (color-coded by kind).
 *  - A **source connector** — short colored segment from the source's anchor
 *    (edge midpoint or node) to the snap point — visually answers "where
 *    did this snap come from?"
 *  - A **source-edge highlight** — thick coloured Line2 along the source edge.
 *  - A **source-node tint** — bumps a SphereMarker's color while a guide is active.
 *  - A **right-angle glyph** at the snap point for perpendicular snaps.
 *  - **Parallel tick marks** for parallel snaps (two short hatches: one across
 *    the source edge midpoint, one at the snap point).
 *
 * Every helper is pooled across frames; nothing is created/destroyed per
 * `onDrag`. Disposal happens on session exit.
 *
 * Coordinate convention: the session passes everything in **absolute world
 * coords** (we do the toAbsolute conversion before calling), so the visuals
 * module never needs to know about reference points.
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Viewer } from '../../../viewer/viewer';
import { PointoramaWallSketch } from '../../../utils/PointoramaWallSketch';
import { ConstructionKind } from './WallSketchSnapEngine';

// Brand palette (mirrors index.css). Kept distinct per construction kind so
// parallel / perpendicular / axis guides stay tellable apart.
const COLOR_PARALLEL = new THREE.Color('#269FC6'); // oil-blue-500
const COLOR_PERPENDICULAR = new THREE.Color('#854DD6'); // french-violet-500
const COLOR_AXIS = new THREE.Color('#97ACAA'); // ash-green-300 — muted neutral

const GUIDE_HALF_LENGTH = 50; // meters; covers any reasonable view extent
const CONNECTOR_OPACITY = 0.85;
const HIGHLIGHT_LINE_WIDTH = 5;
const GLYPH_SIZE = 0.25; // meters; size of the 90° L-glyph arms / parallel tick length
const TICK_HALF = 0.18;

/**
 * Per-line visual bundle. Each ConstructionLine in a snap maps to one of these.
 * Reused across frames; hidden when not needed; disposed only at session end.
 */
interface LineBundle {
  guide: THREE.Line;
  guideMat: THREE.LineDashedMaterial;
  connector: THREE.Line;
  connectorMat: THREE.LineBasicMaterial;
  /** Line2 highlight along the source edge — only positioned + visible when sourceEdgeId is present. */
  edgeHighlight: Line2;
  edgeHighlightMat: LineMaterial;
  /** L-shaped glyph for perpendicular snaps. */
  rightAngle: THREE.Line;
  rightAngleMat: THREE.LineBasicMaterial;
  /** Pair of tick marks for parallel snaps (one near source midpoint, one at snap point). */
  tickSource: THREE.Line;
  tickTarget: THREE.Line;
  tickMat: THREE.LineBasicMaterial;
}

export interface SnapLineRender {
  /** World-coord origin of the construction line. */
  origin: THREE.Vector3;
  /** Unit direction in world space (xy plane). */
  direction: THREE.Vector2;
  kind: ConstructionKind;
  /** World-coord snap point. Same point for every line in an intersection snap. */
  snapPoint: THREE.Vector3;
  /** When set, the source's "from" anchor — connector starts here. */
  sourceAnchor?: THREE.Vector3;
  /** When set, the two endpoints of the source edge for the highlight overlay. */
  sourceEdgeEndpoints?: { a: THREE.Vector3; b: THREE.Vector3 };
  /** Source edge unit direction (used to orient the right-angle glyph). */
  sourceEdgeDirection?: THREE.Vector2;
  /** Source node id — the marker for this node will be tinted in the kind color. */
  sourceNodeId?: string;
}

export class WallSketchSnapVisuals {
  private viewer: Viewer;
  private bundles: LineBundle[] = [];

  /**
   * Per-drag tint we've applied to source SphereMarkers, keyed by node id.
   * Stores the previous color so we can restore on hide. Reset every frame
   * before reapplying so a node that's no longer a source loses its tint.
   */
  private tintedNodes: Map<string, THREE.Color> = new Map();

  /**
   * Returns the parent group new visual children should attach to, or null.
   * The session passes a getter so the parent's world position can shift
   * across edit sessions while bundles remain reusable. When the parent is
   * present, child positions are interpreted as **local to the group**, which
   * keeps line vertex buffers in float32-friendly coords even at UTM scale.
   * When null, falls back to viewer.scene.scene with absolute world coords.
   */
  private getParent: () => THREE.Object3D | null;

  constructor(viewer: Viewer, getParent: () => THREE.Object3D | null) {
    this.viewer = viewer;
    this.getParent = getParent;
  }

  /** Resolve the active parent (helpers group if available, else viewer scene). */
  private resolveParent(): THREE.Object3D {
    return this.getParent() ?? this.viewer.scene.scene;
  }

  /**
   * Update visuals for the active snap lines. Pass an empty array (or call
   * `hide`) when no snap is engaged.
   */
  update(renders: SnapLineRender[], skeleton: PointoramaWallSketch | null): void {
    while (this.bundles.length < renders.length) {
      this.bundles.push(this.createBundle());
    }

    // Restore any node tints from the previous frame that we won't re-apply
    // this frame. Doing this before re-tinting handles the case where a
    // source node disappears between frames (snap changed kind, etc.).
    const stillTinted = new Set<string>();

    for (let i = 0; i < this.bundles.length; i++) {
      const bundle = this.bundles[i];
      const r = renders[i];
      if (!r) {
        this.hideBundle(bundle);
        continue;
      }
      this.renderBundle(bundle, r, skeleton, stillTinted);
    }

    // Restore tints for any node we tinted last frame but didn't this frame.
    for (const [nodeId, prevColor] of this.tintedNodes.entries()) {
      if (!stillTinted.has(nodeId)) {
        const marker = skeleton?.getNodeMarker(nodeId);
        marker?.setColor(prevColor);
        this.tintedNodes.delete(nodeId);
      }
    }
  }

  /**
   * Reset tint on any source node that got recolored between frames. Called
   * from the session whenever `setData` rebuilds the skeleton (which destroys
   * the markers we tinted), so we don't try to restore colors on dead markers.
   */
  forgetTints(): void {
    this.tintedNodes.clear();
  }

  hide(skeleton?: PointoramaWallSketch | null): void {
    for (const bundle of this.bundles) this.hideBundle(bundle);
    // Restore source-node colors. Caller provides the skeleton if the markers
    // are still around; otherwise we just drop our bookkeeping (markers were
    // rebuilt by setData() and their colors are already whatever the skeleton
    // recreated them with).
    for (const [nodeId, prevColor] of this.tintedNodes.entries()) {
      const marker = skeleton?.getNodeMarker(nodeId);
      marker?.setColor(prevColor);
    }
    this.tintedNodes.clear();
  }

  dispose(skeleton: PointoramaWallSketch | null): void {
    // Restore any tinted nodes (best effort — markers may already be gone).
    for (const [nodeId, prevColor] of this.tintedNodes.entries()) {
      const marker = skeleton?.getNodeMarker(nodeId);
      marker?.setColor(prevColor);
    }
    this.tintedNodes.clear();
    for (const bundle of this.bundles) {
      this.disposeBundle(bundle);
    }
    this.bundles = [];
  }

  // ─── Internal ───────────────────────────────────────────────────────

  private createBundle(): LineBundle {
    // Bundle children attach to the active parent (helpers group when
    // editing, viewer scene otherwise). Their geometry positions will be
    // interpreted in the parent's local space — see renderBundle.
    const scene = this.resolveParent();

    // Guide line (dashed, infinite, color-coded by kind)
    const guideGeom = new THREE.BufferGeometry();
    guideGeom.setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
    const guideMat = new THREE.LineDashedMaterial({
      color: 0xffffff,
      dashSize: 0.15,
      gapSize: 0.08,
      depthTest: false,
      transparent: true,
      opacity: 0.55,
    });
    const guide = new THREE.Line(guideGeom, guideMat);
    guide.renderOrder = 996;
    guide.visible = false;
    scene.add(guide);

    // Connector (solid colored line from source to snap)
    const connectorGeom = new THREE.BufferGeometry();
    connectorGeom.setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
    const connectorMat = new THREE.LineBasicMaterial({
      color: 0xffffff,
      depthTest: false,
      transparent: true,
      opacity: CONNECTOR_OPACITY,
    });
    const connector = new THREE.Line(connectorGeom, connectorMat);
    connector.renderOrder = 998;
    connector.visible = false;
    scene.add(connector);

    // Edge highlight (Line2, thicker than the skeleton's own line)
    const ehGeom = new LineGeometry();
    ehGeom.setPositions([0, 0, 0, 0, 0, 0]);
    const renderSize = this.viewer.renderer.getSize(new THREE.Vector2());
    const ehMat = new LineMaterial({
      color: 0xffffff,
      linewidth: HIGHLIGHT_LINE_WIDTH,
      resolution: new THREE.Vector2(renderSize.width || 1000, renderSize.height || 1000),
      depthTest: false,
      transparent: true,
      opacity: 0.85,
    });
    const edgeHighlight = new Line2(ehGeom, ehMat);
    edgeHighlight.computeLineDistances();
    edgeHighlight.renderOrder = 998;
    edgeHighlight.visible = false;
    scene.add(edgeHighlight);

    // Right-angle glyph (3-point polyline forming an L)
    const raGeom = new THREE.BufferGeometry();
    raGeom.setFromPoints([new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()]);
    const raMat = new THREE.LineBasicMaterial({
      color: COLOR_PERPENDICULAR,
      depthTest: false,
      transparent: true,
      opacity: 0.95,
    });
    const rightAngle = new THREE.Line(raGeom, raMat);
    rightAngle.renderOrder = 999;
    rightAngle.visible = false;
    scene.add(rightAngle);

    // Parallel tick marks (two short segments)
    const tickMat = new THREE.LineBasicMaterial({
      color: COLOR_PARALLEL,
      depthTest: false,
      transparent: true,
      opacity: 0.95,
    });
    const tickSourceGeom = new THREE.BufferGeometry();
    tickSourceGeom.setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
    const tickSource = new THREE.Line(tickSourceGeom, tickMat);
    tickSource.renderOrder = 999;
    tickSource.visible = false;
    scene.add(tickSource);

    const tickTargetGeom = new THREE.BufferGeometry();
    tickTargetGeom.setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
    const tickTarget = new THREE.Line(tickTargetGeom, tickMat);
    tickTarget.renderOrder = 999;
    tickTarget.visible = false;
    scene.add(tickTarget);

    return {
      guide,
      guideMat,
      connector,
      connectorMat,
      edgeHighlight,
      edgeHighlightMat: ehMat,
      rightAngle,
      rightAngleMat: raMat,
      tickSource,
      tickTarget,
      tickMat,
    };
  }

  private renderBundle(bundle: LineBundle, r: SnapLineRender, skeleton: PointoramaWallSketch | null, stillTinted: Set<string>): void {
    const color = colorForKind(r.kind);

    // The bundle's THREE objects are children of `resolveParent()`. When the
    // parent has a non-zero world position (helpers group anchored at the
    // session reference point), we must write geometry coords *relative to*
    // the parent. `localize` does that subtraction once and we run every
    // world-space input through it.
    const parent = this.resolveParent();
    const ox = parent.position.x;
    const oy = parent.position.y;
    const oz = parent.position.z;
    const localize = (v: { x: number; y: number; z: number }): THREE.Vector3 => new THREE.Vector3(v.x - ox, v.y - oy, v.z - oz);

    // Guide
    bundle.guideMat.color.copy(color);
    const start = localize({ x: r.origin.x - r.direction.x * GUIDE_HALF_LENGTH, y: r.origin.y - r.direction.y * GUIDE_HALF_LENGTH, z: r.origin.z });
    const end = localize({ x: r.origin.x + r.direction.x * GUIDE_HALF_LENGTH, y: r.origin.y + r.direction.y * GUIDE_HALF_LENGTH, z: r.origin.z });
    setLinePoints(bundle.guide, start, end);
    bundle.guide.computeLineDistances();
    bundle.guide.visible = true;

    // Connector — only when we have a source anchor
    if (r.sourceAnchor) {
      bundle.connectorMat.color.copy(color);
      setLinePoints(bundle.connector, localize(r.sourceAnchor), localize(r.snapPoint));
      bundle.connector.visible = true;
    } else {
      bundle.connector.visible = false;
    }

    // Source edge highlight
    if (r.sourceEdgeEndpoints) {
      const a = localize(r.sourceEdgeEndpoints.a);
      const b = localize(r.sourceEdgeEndpoints.b);
      bundle.edgeHighlightMat.color.copy(color);
      (bundle.edgeHighlight.geometry as LineGeometry).setPositions([a.x, a.y, a.z, b.x, b.y, b.z]);
      bundle.edgeHighlight.computeLineDistances();
      bundle.edgeHighlight.visible = true;
    } else {
      bundle.edgeHighlight.visible = false;
    }

    // Right-angle glyph (perpendicular only)
    if (r.kind === 'perpendicular' && r.sourceEdgeDirection) {
      const sd = r.sourceEdgeDirection;
      // The L's two arms: one along the source-edge direction, one along its
      // perpendicular (which is also the candidate's direction at the snap).
      // We pick the orientation that points back toward the source so the L
      // sits in the corner facing the existing geometry.
      const towardSource = r.sourceAnchor
        ? new THREE.Vector2(r.sourceAnchor.x - r.snapPoint.x, r.sourceAnchor.y - r.snapPoint.y).normalize()
        : new THREE.Vector2(-sd.y, sd.x);
      const sign1 = Math.sign(sd.x * towardSource.x + sd.y * towardSource.y) || 1;
      const arm1 = new THREE.Vector2(sd.x * sign1, sd.y * sign1).multiplyScalar(GLYPH_SIZE);
      const perp = new THREE.Vector2(-sd.y, sd.x);
      const sign2 = Math.sign(perp.x * towardSource.x + perp.y * towardSource.y) || 1;
      const arm2 = new THREE.Vector2(perp.x * sign2, perp.y * sign2).multiplyScalar(GLYPH_SIZE);
      const cz = r.snapPoint.z + 0.005;
      const p1 = localize({ x: r.snapPoint.x + arm1.x, y: r.snapPoint.y + arm1.y, z: cz });
      const corner = localize({ x: r.snapPoint.x + arm1.x + arm2.x, y: r.snapPoint.y + arm1.y + arm2.y, z: cz });
      const p2 = localize({ x: r.snapPoint.x + arm2.x, y: r.snapPoint.y + arm2.y, z: cz });
      const positions = bundle.rightAngle.geometry.attributes.position as THREE.BufferAttribute;
      positions.setXYZ(0, p1.x, p1.y, p1.z);
      positions.setXYZ(1, corner.x, corner.y, corner.z);
      positions.setXYZ(2, p2.x, p2.y, p2.z);
      positions.needsUpdate = true;
      bundle.rightAngleMat.color.copy(color);
      bundle.rightAngle.visible = true;
    } else {
      bundle.rightAngle.visible = false;
    }

    // Parallel tick marks (parallel only) — one across source midpoint, one at snap point.
    // Each tick is short, perpendicular to the line's direction.
    if (r.kind === 'parallel') {
      const perp = new THREE.Vector2(-r.direction.y, r.direction.x);
      const cz = r.snapPoint.z + 0.005;
      const sourceMid = r.sourceEdgeEndpoints
        ? { x: (r.sourceEdgeEndpoints.a.x + r.sourceEdgeEndpoints.b.x) / 2, y: (r.sourceEdgeEndpoints.a.y + r.sourceEdgeEndpoints.b.y) / 2, z: cz }
        : r.sourceAnchor;

      bundle.tickMat.color.copy(color);

      if (sourceMid) {
        setLinePoints(
          bundle.tickSource,
          localize({ x: sourceMid.x - perp.x * TICK_HALF, y: sourceMid.y - perp.y * TICK_HALF, z: cz }),
          localize({ x: sourceMid.x + perp.x * TICK_HALF, y: sourceMid.y + perp.y * TICK_HALF, z: cz }),
        );
        bundle.tickSource.visible = true;
      } else {
        bundle.tickSource.visible = false;
      }

      setLinePoints(
        bundle.tickTarget,
        localize({ x: r.snapPoint.x - perp.x * TICK_HALF, y: r.snapPoint.y - perp.y * TICK_HALF, z: cz }),
        localize({ x: r.snapPoint.x + perp.x * TICK_HALF, y: r.snapPoint.y + perp.y * TICK_HALF, z: cz }),
      );
      bundle.tickTarget.visible = true;
    } else {
      bundle.tickSource.visible = false;
      bundle.tickTarget.visible = false;
    }

    // Source node tint — done last so all per-line state is rendered first.
    if (skeleton) this.tintSourceNode(skeleton, r, color, stillTinted);
  }

  private hideBundle(bundle: LineBundle): void {
    bundle.guide.visible = false;
    bundle.connector.visible = false;
    bundle.edgeHighlight.visible = false;
    bundle.rightAngle.visible = false;
    bundle.tickSource.visible = false;
    bundle.tickTarget.visible = false;
  }

  private disposeBundle(bundle: LineBundle): void {
    // Use removeFromParent so we detach from whatever parent the bundle is
    // currently under (helpers group most of the time, viewer scene as a
    // fallback). Three.js handles a missing parent silently.
    for (const obj of [bundle.guide, bundle.connector, bundle.edgeHighlight, bundle.rightAngle, bundle.tickSource, bundle.tickTarget]) {
      obj.removeFromParent();
      (obj as THREE.Line | Line2).geometry.dispose();
    }
    bundle.guideMat.dispose();
    bundle.connectorMat.dispose();
    bundle.edgeHighlightMat.dispose();
    bundle.rightAngleMat.dispose();
    bundle.tickMat.dispose();
  }

  private tintSourceNode(skeleton: PointoramaWallSketch, r: SnapLineRender, color: THREE.Color, stillTinted: Set<string>): void {
    if (!r.sourceNodeId) return;
    const marker = skeleton.getNodeMarker(r.sourceNodeId);
    if (!marker) return;
    // First time we tint a given node, remember the color it had so we can
    // restore it on hide / kind-change.
    if (!this.tintedNodes.has(r.sourceNodeId)) {
      // Best we can do: pull the current uniform color from the marker. There's
      // no public getter so we read the shader uniform directly. The cast is
      // safe because SphereMarker uses ShaderMaterial under the hood.
      const mat = marker.mesh.material as THREE.ShaderMaterial;
      const prev = (mat.uniforms.uColor.value as THREE.Color).clone();
      this.tintedNodes.set(r.sourceNodeId, prev);
    }
    marker.setColor(color);
    stillTinted.add(r.sourceNodeId);
  }
}

function colorForKind(kind: ConstructionKind): THREE.Color {
  switch (kind) {
    case 'parallel':
      return COLOR_PARALLEL;
    case 'perpendicular':
      return COLOR_PERPENDICULAR;
    default:
      return COLOR_AXIS;
  }
}

function setLinePoints(line: THREE.Line, a: THREE.Vector3, b: THREE.Vector3): void {
  const positions = line.geometry.attributes.position as THREE.BufferAttribute;
  positions.setXYZ(0, a.x, a.y, a.z);
  positions.setXYZ(1, b.x, b.y, b.z);
  positions.needsUpdate = true;
}
